import { Check, CheckCheck, Clock, AlertCircle } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatMessageTime } from '@/utils/formatters'
import type { Message } from '@/types'
import { AudioMessagePlayer } from './AudioMessagePlayer'

interface MessageBubbleProps {
  message: Message
  showAvatar?: boolean
}

function MessageStatus({ status }: { status: Message['status'] }) {
  const base = 'inline-flex'
  switch (status) {
    case 'pending':
      return <Clock size={12} className={cn(base, 'text-white/60')} />
    case 'sent':
      return <Check size={12} className={cn(base, 'text-white/70')} />
    case 'delivered':
      return <CheckCheck size={12} className={cn(base, 'text-white/70')} />
    case 'read':
      return <CheckCheck size={12} className={cn(base, 'text-blue-200')} />
    case 'failed':
      return (
        <span title="Falha no envio">
          <AlertCircle
            size={12}
            className={cn(base, 'text-red-300')}
          />
        </span>
      )
    default:
      return null
  }
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isOutbound = message.direction === 'outbound'
  const shouldRenderTextFallback =
    (message.message_type === 'carousel' ||
      message.message_type === 'buttons' ||
      message.message_type === 'list' ||
      message.message_type === 'reaction') &&
    !!message.content

  return (
    <div
      className={cn(
        'mb-2 grid w-full',
        isOutbound ? 'justify-items-end' : 'justify-items-start',
      )}
    >
      <div
        className={cn(
          'relative w-fit max-w-[min(72%,38rem)] px-4 py-3 shadow-[0_12px_32px_rgba(10,10,30,0.18)]',
          isOutbound ? 'bubble-outbound' : 'bubble-inbound',
        )}
      >
        {/* Message content */}
        {message.message_type === 'text' && message.content && (
          <p
            className={cn(
              'text-sm leading-relaxed whitespace-pre-wrap break-words',
              isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
            )}
          >
            {message.content}
          </p>
        )}

        {message.message_type === 'image' && message.media_url && (
          <div className="space-y-1.5">
            <img
              src={message.media_url}
              alt="Imagem"
              className="block max-h-[320px] max-w-[280px] rounded-[var(--radius-sm)] object-cover"
            />
            {message.content && (
              <p
                className={cn(
                  'text-sm',
                  isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
                )}
              >
                {message.content}
              </p>
            )}
          </div>
        )}

        {message.message_type !== 'text' && message.message_type !== 'image' && message.content && (
          <p
            className={cn(
              'mb-2 text-sm leading-relaxed whitespace-pre-wrap break-words',
              isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
            )}
          >
            {message.content}
          </p>
        )}

        {message.message_type === 'audio' && (
          <AudioMessagePlayer message={message} isOutbound={isOutbound} />
        )}

        {message.message_type === 'document' && (
          <div className={cn(
            'flex items-center gap-2 text-sm',
            isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
          )}>
            {message.media_url ? (
              <a
                href={message.media_url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'underline',
                  isOutbound ? 'text-white' : 'text-[var(--color-brand)]',
                )}
              >
                Documento
              </a>
            ) : (
              <span className="opacity-80">Documento indisponível</span>
            )}
          </div>
        )}

        {shouldRenderTextFallback && (
          <p
            className={cn(
              'text-sm leading-relaxed whitespace-pre-wrap break-words',
              isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
            )}
          >
            {message.content}
          </p>
        )}

        {/* Timestamp + status */}
        <div
          className={cn(
            'mt-1.5 flex items-center gap-1 justify-end',
          )}
        >
          <span
            className={cn(
              'text-[10px]',
              isOutbound ? 'text-white/60' : 'text-[var(--text-muted)]',
            )}
          >
            {formatMessageTime(message.created_at)}
          </span>
          {isOutbound && <MessageStatus status={message.status} />}
        </div>
      </div>
    </div>
  )
}
